import { Injectable } from "@angular/core";
import { Sort } from "@angular/material/sort";
import { LookupModel } from "@app/configuration/models/configuration.models";
import { PagedList } from "@app/core";
import { GrievanceListService } from "@app/grievances/list/services";
import { GetGrievanceListPayload, Grievance, GrievanceListFilterOptions, GrievanceListFilters } from "@app/grievances/list/types";
import { RootStoreState, ViewStateComponentStore, ViewStateComponentStoreState } from "@app/root-store";
import { Pagination } from "@app/types/core";
import { PopoverFilterOption } from '@app/shared/filters';
import { tapResponse } from '@ngrx/component-store';
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { concatMap, switchMap, tap, withLatestFrom } from "rxjs/operators";


export interface GrievanceListState extends ViewStateComponentStoreState {
   grievances: Grievance[];
   payload: GetGrievanceListPayload;
   options: GrievanceListFilterOptions;
   userRoles: LookupModel[];
   userRolesResponses: Response[];
   count: number;
   loading: boolean,
   toggle: boolean
}

export interface GrievanceListViewState {
   filters: GrievanceListFilters;
   search: string;
   activeOnly: boolean;
   pagination: Pagination;
   sort: Sort;
}

const initialState: GrievanceListState = {
   grievances: [],
   payload: {
      pagination: null,
      sort: null,
      filters: {},
      search: null
   },
   options: {
      fundingPrograms: null,
      statuses: null
   },
   userRoles: [],
   userRolesResponses: [],
   count: 0,
   loading: false,
   toggle: false,
   viewId: null
}

const initialViewState: GrievanceListViewState = {
   filters: {
      grievant: null,
      case: null,
      funder: null,
      status: null,
      fileDate: null,
      escalationDate: null,
      replyDueDate: null,
      actualReplyDate: null
   },
   search: null,
   activeOnly: false,
   pagination: { page: 1, pageSize: 25 },
   sort: { active: 'fileDate', direction: 'desc' }
};

@Injectable()
export class GrievanceStateStore extends ViewStateComponentStore<GrievanceListState, GrievanceListViewState> {

   constructor(
      rootStore: Store<RootStoreState.State>,
      private service: GrievanceListService,) {
      super(rootStore, initialViewState);
   }

   init(viewId: string): void {
      this.initCore({
         ...initialState,
         viewId
      });
      this.patchState({ loading: true });
      this.service.getGrievanceListOptions().pipe(
         tapResponse(options =>
            this.patchState({ options, loading: false }), error => console.log(error))).subscribe();
   }

   readonly funderOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.fundingPrograms?.map(value => ({ value, text: value }))
   );

   readonly statusOptions$: Observable<PopoverFilterOption[]> = this.select(
      this.state$,
      ({ options }) => options.statuses?.map(value => ({ value, text: value }))
   );

   readonly vm$ = this.select(
      this.funderOptions$,
      this.statusOptions$,
      this.state$,
      this.viewState$,
      (funderOptions, statusOptions, { grievances, payload, userRoles, count, loading, toggle }, { filters, activeOnly, pagination, sort }) => (
         { funderOptions, statusOptions, grievances, payload, userRoles, count, loading, toggle, filters, activeOnly, pagination, sort })
   )

   readonly updateGrievances = this.updater((state, { list, count }: PagedList<Grievance>): GrievanceListState => ({
      ...state,
      grievances: list,
      count,
      loading: false
   }));

   readonly reload = this.effect((origin$: Observable<GetGrievanceListPayload>) => origin$.pipe(
      tap(payload => this.patchState({ payload, loading: true })),
      switchMap(payload => this.service.getGrievancesList(payload).pipe(
         tapResponse(({ count, page, pageSize, list }) => this.updateGrievances({ list, page, pageSize, count }), err => console.log(err))
      ))
   ))(this.viewState$);

   readonly addGrievance = this.updater((state, grievance: Grievance) => ({
      ...state,
      grievances: state.grievances.filter(g => g.id !== grievance.id).concat(grievance)
   }));

   readonly deleteGrievance = this.effect((origin$: Observable<string>) => origin$.pipe(
      tap(() => this.patchState({ loading: true })),
      concatMap(id => this.service.deleteGrievance(id)),
      withLatestFrom(this.viewState$),
      concatMap(([_, payload]) => this.service.getGrievancesList(payload)),
      tapResponse(({ count, page, pageSize, list }) => this.updateGrievances({ list, page, pageSize, count }), err => console.log(err))
   ));
}